import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { isApiError } from '@/api/errors';
import { fetchVault, rotateVault } from '@/api/ops';
import type { Rotation } from '@/api/opsTypes';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { QueryError } from '@/ui/QueryError';

export function VaultSection() {
  const query = useQuery({ queryKey: ['maintenance', 'vault'], queryFn: fetchVault });
  const [result, setResult] = useState<Rotation | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vault key rotation</CardTitle>
        <CardDescription>
          Re-encrypts stored secrets under the current key. Rotate again until a run moves nothing;
          only then can the old key be removed from configuration.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {query.isError ? (
          <QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : query.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <p className="text-sm">
              Current key version: <strong>{query.data.current_key_version}</strong>
            </p>
            {query.data.rotatable_key_versions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No older key version to rotate from.</p>
            ) : (
              query.data.rotatable_key_versions.map((version) => (
                <RotateDialog key={version} version={version} onDone={setResult} />
              ))
            )}
          </>
        )}
        {result && (
          <Alert>
            <AlertDescription>
              Moved {result.total} secret(s) from {result.from_key_version} to{' '}
              {result.to_key_version} ({result.platform_tokens} platform tokens,{' '}
              {result.subscription_secrets} subscription secrets, {result.admin_totp_secrets} admin
              TOTP secrets).{' '}
              {result.complete
                ? 'Complete: nothing is left under the old key.'
                : 'Not complete yet — rotate again.'}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function RotateDialog({
  version,
  onDone,
}: {
  version: string;
  onDone: (result: Rotation) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');

  const rotate = useMutation({
    mutationFn: () => rotateVault(version, typed),
    onSuccess: (result) => {
      onDone(result);
      void queryClient.invalidateQueries({ queryKey: ['maintenance', 'vault'] });
      setOpen(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setTyped('');
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="w-fit">
          Rotate from {version}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rotate from {version}</DialogTitle>
          <DialogDescription>
            This re-encrypts every secret stored under {version}. Type <strong>{version}</strong> to
            confirm.
          </DialogDescription>
        </DialogHeader>
        {rotate.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {isApiError(rotate.error) ? rotate.error.message : 'Rotation failed.'}
            </AlertDescription>
          </Alert>
        )}
        <Field>
          <FieldLabel htmlFor="rotate-confirm">Confirm key version</FieldLabel>
          <Input
            id="rotate-confirm"
            autoComplete="off"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
          <FieldError />
        </Field>
        <DialogFooter>
          <Button disabled={typed !== version || rotate.isPending} onClick={() => rotate.mutate()}>
            {rotate.isPending ? 'Rotating…' : 'Rotate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
